import React, { Fragment, useEffect } from 'react';
import classNames from 'classnames';
import T from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParam } from 'react-use';
import { Wizard } from 'react-use-wizard';
import BreadcrumbStep from '../../../components/wizard/BreadcrumbStep';
import Breadcrumbs from '../../../components/wizard/Breadcrumbs';
import { MEMBERSHIP } from '../../../constants/membership';
import useBreakpoint from '../../../hooks/useBreakpoint';
import {
  setMembershipPriceId,
  setPaymentMethodId,
} from '../../../redux/actions/order';
import { profileSelectors } from '../../../redux/selectors/profile';
import {
  isStaff,
  isUserUnderTrial,
  userHasValidMembership
} from '../../../utilities/user';
import { createOrderSteps, filterAvailableSteps } from './orderStepsFactory';

const getStartingStep = (step, steps) => {
  let startingStep = 1;
  if (step < steps.length) {
    startingStep = typeof step === 'string' ? !isNaN(+step) && +step : step;
  }
  return startingStep;
};

const OrderWizard = ({
  isOnlyBreadcrumbs = false,
  defaultStep = 1,
  isVisible = true,
}) => {
  const initialStep = useSearchParam('step');
  const isBreakpointEnabled = useBreakpoint(['xs', 'sm']);
  const dispatch = useDispatch();
  const { job, paymentMethodId, currentUser, membershipPriceId } = useSelector(
    (state) => ({
      job: state.jobsReducer.job,
      membershipPriceId: state.order.membershipPriceId,
      paymentMethodId: state.order.paymentMethodId,
      currentUser: state.profileReducer.userProfile,
    })
  );
  const hasValidMembership = useSelector(profileSelectors.hasValidMembership);

  useEffect(() => {
    if (
      (currentUser && isStaff(currentUser.role)) ||
      (userHasValidMembership(currentUser) && !isUserUnderTrial(currentUser))
    ) {
      dispatch(setMembershipPriceId(MEMBERSHIP.NO_MEMBERSHIP));
    }
  }, [currentUser, dispatch]);

  // TODO: RE-IMPLEMENT
  // const isRequestQuote = useMemo(
  //   () => buyNowData.length === 0 && !isMembershipPriceId(membershipPriceId),
  //   [buyNowData.length, membershipPriceId]
  // );

  if (!job) {
    return null;
  }

  const handlePreviousStepClicked = async (previousStep) => {
    previousStep();
  };

  const orderSteps = createOrderSteps({
    isBreakpointEnabled,
    membershipPriceId,
    paymentMethodId,
    currentUser,
    job,
    hasValidMembership,
    dispatch,
    setMembershipPriceId,
    setPaymentMethodId,
    handlePreviousStepClicked,
  });

  const availableSteps = filterAvailableSteps(orderSteps);

  return (
    <div className={classNames('OrderWizard', isVisible && 'visible')}>
      <Wizard
        startIndex={getStartingStep(initialStep ?? defaultStep, availableSteps)}
        header={
          <Breadcrumbs>
            {availableSteps.map(({ name, disabled }, index) => (
              <BreadcrumbStep
                key={index}
                stepIndex={index}
                stepLabel={index + 1}
                name={name}
                disabled={disabled || isOnlyBreadcrumbs}
              />
            ))}
          </Breadcrumbs>
        }
      >
        {availableSteps.map(({ component }, index) => (
          <Fragment key={index}>{!isOnlyBreadcrumbs && component}</Fragment>
        ))}
      </Wizard>
    </div>
  );
};

OrderWizard.propTypes = {
  isOnlyBreadcrumbs: T.bool,
  defaultStep: T.number,
  isVisible: T.bool,
};

export default OrderWizard;
